"use client";

import React from "react";
import {Stack, Box, Paper, Collapse, useTheme, ClickAwayListener} from "@mui/material";
import PrimaryNavigation from "@/components/header/navigation/desktop/_components/primary-navigation/primary-navigation";
import { HeaderNavProps } from "@/components/header/common/types";
import ColletteLogo from "@/components/collette-logo/collette-logo";
import {Company} from "@/types/contentful/company";
import {Locale} from "@/i18n/routing";
import SecondaryNavigation
  from "@/components/header/navigation/desktop/_components/secondary-navigation/secondary-navigation";
import TertiaryNavigationPane
  from "@/components/header/navigation/desktop/_components/tertiary-navigation/tertiary-pane";
import useDesktopNavbar from "@/hooks/navigation/use-desktop-navbar";

interface DesktopNavbarProps {
  headerNavProps: HeaderNavProps[];
  companyList: Company[]
  locale: Locale;
}

export default function DesktopNavbar(props: DesktopNavbarProps) {
  const { headerNavProps, companyList, locale } = props;

  const {
    handleCloseDrawer,
    handleToggleDrawer,
    tertiaryHeaderLearnMoreUrl,
    tertiaryHeaderViewAllDestinationsUrl,
    tertiaryHeaderLabel,
    secondaryNavItems,
    tertiaryNavItems,
    drawerOpen,
    currentPrimaryNavItem,
    setTertiaryNavItems,
    setTertiaryHeaderLabel,
    setTertiaryHeaderLearnMoreUrl,
    setTertiaryHeaderViewAllDestinationsUrl
  } = useDesktopNavbar({
    headerNavProps
  });

  const theme = useTheme();
  const paddingTop = (theme.mixins?.toolbar?.minHeight as number) + 64;

  return (
    <ClickAwayListener onClickAway={handleCloseDrawer}>
      <Stack>
        <Stack
          data-testid="navbar-container"
          direction="row"
          sx={{
            pt: 10,
            pb: 3
          }}
        >
          <Box sx={{
            pr: 4,
            ml: { md: 2, lg: 0 },
            "@media (max-width: 1270px)": {
              ml: 3
            },
            flexShrink: 0,
            filter: "var(--isagent-logo-color)"
          }}>
            <ColletteLogo
              company={companyList[0]}
              logoType="headerLogo"
              fallbackurl="/images/collette-header-logo.png"
              height={35}
              width={140}
              locale={locale}
            />
          </Box>
          <Box sx={{ pr: { xs: 0, lg: "325px" } }}>
            <Stack
              sx={{
                display: "flex",
                alignItems: "center",
                overflow: "hidden"
              }}>
              <Box>
                <PrimaryNavigation
                  headerNavProps={headerNavProps}
                  handleItemClick={handleToggleDrawer}
                  currentPrimaryNavItem={currentPrimaryNavItem}
                  drawerOpen={drawerOpen}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
        <Box
          sx={{
            display: { sm: "none", md: "block" },
            position: "fixed",
            top: `calc(${paddingTop}px + 2.1rem + var(--banner-height, 0px))`,
            width: "100%",
            maxWidth: "1200px",
            marginRight: 200,
            zIndex: 999,
            left: 0,
            right: 0,
            mx: "auto",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px"
          }}
        >
          <Collapse
            id="navigation-drawer"
            in={drawerOpen}
            timeout={300}
            onClick={(e) => e.stopPropagation()}
            sx={{
              boxShadow: 8,
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px"
            }}
          >
            <Box
              role="presentation"
              sx={{
                backgroundColor: "background.default",
                overflow: "hidden",
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                height: "auto",
                maxHeight: "530px",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px"
              }}
            >
              <Paper
                sx={{
                  backgroundColor: "background.default",
                  display: "flex",
                  flex: 1,
                  mx: "auto",
                  pt: { xs: "var(--Header-height)", md: 0 },
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  maxHeight: "inherit"
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "24.5%",
                    height: "100%",
                    display: { sm: "none", md: "block" },
                    boxShadow: 8,
                    borderBottomLeftRadius: "8px",
                    maxHeight: "inherit"
                  }}
                >
                  <SecondaryNavigation
                    secondaryNavItems={secondaryNavItems}
                    setTertiaryNavItems={setTertiaryNavItems}
                    setTertiaryHeaderLabel={setTertiaryHeaderLabel}
                    setTertiaryHeaderLearnMoreUrl={setTertiaryHeaderLearnMoreUrl}
                    setTertiaryHeaderViewAllDestinationsUrl={setTertiaryHeaderViewAllDestinationsUrl}
                  />
                </Box>
                <Box sx={{ width: "75.5%" }}>
                   <TertiaryNavigationPane
                    tertiaryHeaderLabel={tertiaryHeaderLabel}
                    tertiaryHeaderLearnMoreUrl={tertiaryHeaderLearnMoreUrl}
                    tertiaryHeaderViewAllDestinationsUrl={tertiaryHeaderViewAllDestinationsUrl}
                    tertiaryNavItems={tertiaryNavItems}
                   />
                </Box>
              </Paper>
            </Box>
          </Collapse>
        </Box>
      </Stack>
    </ClickAwayListener>
  );
}
